import {
  IconArrowLeft,
  IconAlertTriangle,
  IconBell,
  IconBuilding,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconCloud,
  IconDatabase,
  IconDownload,
  IconInbox,
  IconKey,
  IconLock,
  IconMail,
  IconMailQuestion,
  IconMoon,
  IconPaperclip,
  IconPalette,
  IconPlus,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlugConnected,
  IconRefresh,
  IconSearch,
  IconSend,
  IconServer,
  IconShieldCheck,
  IconSettings,
  IconSun,
  IconTrash,
  IconUser,
  IconUserEdit,
  IconUserOff,
  IconUserShield,
  IconWorld,
  IconX,
} from "@tabler/icons-preact";
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type {
  EmailUsagePeriod,
  EmailUsageResponse,
  MailConnection,
  MailConnectionsResponse,
  MailDiagnosticMessage,
  MailDiagnosticsDirection,
  MailDiagnosticsPeriod,
  MailDiagnosticsResponse,
  MailDiagnosticsState,
  MailInitialSyncScope,
  SessionResponse,
  SettingsOverviewResponse,
  UnmatchedMessagesResponse,
} from "../shared/contracts";
import { apiRequest } from "./api";
import { EmailDocumentFrame } from "./EmailDocumentFrame";
import type { BackgroundNotificationStatus, MailNotificationPermission } from "./use-mail-notifications";

export type SettingsRoute = "overview" | "profile" | "password" | "appearance" | "notifications" | "mailboxes" | "domains" | "unmatched" | "connections" | "usage";
export type PowerMailTheme = "light" | "dark";

const routePaths: Record<SettingsRoute, string> = {
  overview: "/configuracoes",
  profile: "/configuracoes/perfil",
  password: "/configuracoes/seguranca",
  appearance: "/configuracoes/aparencia",
  notifications: "/configuracoes/notificacoes",
  mailboxes: "/configuracoes/caixas",
  domains: "/configuracoes/dominios",
  unmatched: "/configuracoes/nao-cadastrados",
  connections: "/configuracoes/conexoes",
  usage: "/configuracoes/uso",
};

export function settingsPath(route: SettingsRoute): string {
  return routePaths[route];
}

export function settingsRouteFromPath(pathname: string): SettingsRoute | null {
  const entry = (Object.entries(routePaths) as Array<[SettingsRoute, string]>)
    .find(([, path]) => pathname === path || (path !== "/configuracoes" && pathname.startsWith(`${path}/`)));
  if (entry) return entry[0];
  return pathname === "/configuracoes/" ? "overview" : null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || "P"}${parts.length > 1 ? parts.at(-1)?.[0] || "" : parts[0]?.[1] || "M"}`.toLocaleUpperCase("pt-BR");
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1_024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
  return `${(bytes / 1_073_741_824).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} GB`;
}

function roleName(session: SessionResponse): string {
  if (session.account.isRoot) return "Root PowerMail";
  if (session.tenant.role === "owner" || session.tenant.role === "admin") return "Administrador";
  return "Usuário";
}

function temporaryPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = new Uint8Array(14);
  crypto.getRandomValues(bytes);
  return `Pm!9${Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join("")}`;
}

interface SettingsPageProps {
  route: SettingsRoute;
  session: SessionResponse;
  theme: PowerMailTheme;
  onBack: () => void;
  onRouteChange: (route: SettingsRoute) => void;
  onSessionRefresh: () => void;
  onThemeChange: (theme: PowerMailTheme) => void;
  onToast: (message: string) => void;
  notifications: {
    permission: MailNotificationPermission;
    status: BackgroundNotificationStatus;
    loading: boolean;
    selectedMailboxIds: string[];
    onActivate: () => Promise<BackgroundNotificationStatus>;
    onSave: (mailboxIds: string[]) => Promise<void>;
  };
}

interface SettingsNavItem {
  route: SettingsRoute;
  label: string;
  description: string;
  icon: ComponentChildren;
}

function SettingsNav({ route, session, onBack, onRouteChange }: Pick<SettingsPageProps, "route" | "session" | "onBack" | "onRouteChange">) {
  const accountItems: SettingsNavItem[] = [
    { route: "overview", label: "Visão geral", description: "Central de ajustes", icon: <IconSettings size={17} /> },
    { route: "profile", label: "Perfil", description: "Identidade e acesso", icon: <IconUser size={17} /> },
    { route: "password", label: "Segurança", description: "Senha da conta", icon: <IconLock size={17} /> },
    { route: "appearance", label: "Aparência", description: "Tema da interface", icon: <IconPalette size={17} /> },
    { route: "notifications", label: "Notificações", description: "Caixas que podem avisar", icon: <IconBell size={17} /> },
  ];
  const canManageOperation = session.account.isRoot || session.tenant.role === "owner" || session.tenant.role === "admin";
  const operationItems: SettingsNavItem[] = [
    { route: "mailboxes", label: canManageOperation ? "Caixas de e-mail" : "Minha caixa", description: canManageOperation ? "Uso, acessos e endereços" : "Uso do seu e-mail", icon: <IconMail size={17} /> },
    ...(canManageOperation ? [{ route: "domains" as const, label: "Domínios", description: session.account.isRoot ? "Cadastro global" : "Domínios da empresa", icon: <IconWorld size={17} /> }] : []),
    ...(canManageOperation ? [{ route: "unmatched" as const, label: "Não cadastrados", description: "Mensagens sem caixa", icon: <IconMailQuestion size={17} /> }] : []),
    ...(session.account.isRoot ? [{ route: "connections" as const, label: "Conexões IMAP", description: "Importações e sincronização", icon: <IconPlugConnected size={17} /> }] : []),
    ...(session.account.isRoot ? [{ route: "usage" as const, label: "Uso de e-mail", description: "Limites e estatísticas", icon: <IconChartBar size={17} /> }] : []),
  ];

  const renderItems = (items: SettingsNavItem[]) => items.map((item) => (
    <button className={route === item.route ? "is-active" : ""} type="button" key={item.route} onClick={() => onRouteChange(item.route)}>
      <span>{item.icon}</span>
      <span><strong>{item.label}</strong><small>{item.description}</small></span>
      <IconChevronRight size={14} aria-hidden="true" />
    </button>
  ));

  return (
    <aside className="settings-nav">
      <button className="settings-nav__back" type="button" onClick={onBack}><IconArrowLeft size={16} /> Voltar ao e-mail</button>
      <div className="settings-nav__identity">
        <span>{initials(session.account.name)}</span>
        <div><strong>{session.account.name}</strong><small>{roleName(session)}</small></div>
      </div>
      <nav aria-label="Navegação das configurações">
        <section><span>Minha conta</span>{renderItems(accountItems)}</section>
        <section><span>Operação</span>{renderItems(operationItems)}</section>
      </nav>
      <footer><IconDatabase size={15} /><span><strong>Cloudflare ativa</strong><small>{session.tenant.name}</small></span></footer>
    </aside>
  );
}

function PageHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: ComponentChildren }) {
  return (
    <header className="settings-heading">
      <div><span>{eyebrow}</span><h1>{title}</h1><p>{description}</p></div>
      {action ? <div>{action}</div> : null}
    </header>
  );
}

function SettingsOverview({ session, data, onRouteChange }: { session: SessionResponse; data: SettingsOverviewResponse | null; onRouteChange: (route: SettingsRoute) => void }) {
  const activeMailbox = session.mailboxes.find((mailbox) => mailbox.id === session.activeMailboxId);
  const canManageOperation = session.account.isRoot || session.tenant.role === "owner" || session.tenant.role === "admin";
  return (
    <div className="settings-overview">
      <PageHeader eyebrow="Configurações" title="Seu espaço no PowerMail" description="Conta, aparência e operação de e-mail organizadas em um só lugar." />
      <section className="settings-overview__hero">
        <div><span className="settings-overview__avatar">{initials(session.account.name)}</span><div><small>Conta conectada</small><strong>{session.account.name}</strong><span>{session.account.email}</span></div></div>
        <dl>
          <div><dt>Caixa em uso</dt><dd>{activeMailbox?.address || "Não definida"}</dd></div>
          <div><dt>Nível de acesso</dt><dd>{roleName(session)}</dd></div>
          <div><dt>Armazenamento</dt><dd>{data ? formatBytes(data.totals.storageBytes) : "Calculando…"}</dd></div>
        </dl>
      </section>
      <section className="settings-category">
        <header><div><span>01</span><div><strong>Conta e preferências</strong><small>Dados pessoais, segurança e aparência.</small></div></div></header>
        <div className="settings-category__links">
          <button type="button" onClick={() => onRouteChange("profile")}><IconUser size={19} /><span><strong>Perfil</strong><small>Veja sua identidade e empresa ativa</small></span><IconChevronRight size={16} /></button>
          <button type="button" onClick={() => onRouteChange("password")}><IconLock size={19} /><span><strong>Segurança</strong><small>Altere a senha de acesso</small></span><IconChevronRight size={16} /></button>
          <button type="button" onClick={() => onRouteChange("appearance")}><IconPalette size={19} /><span><strong>Aparência</strong><small>Escolha entre tema claro e escuro</small></span><IconChevronRight size={16} /></button>
          <button type="button" onClick={() => onRouteChange("notifications")}><IconBell size={19} /><span><strong>Notificações</strong><small>Escolha quais caixas podem enviar avisos</small></span><IconChevronRight size={16} /></button>
        </div>
      </section>
      <section className="settings-category settings-category--operation">
        <header><div><span>02</span><div><strong>Operação de e-mail</strong><small>Endereços, domínios, mensagens e armazenamento.</small></div></div></header>
        <div className="settings-category__links">
          <button type="button" onClick={() => onRouteChange("mailboxes")}><IconMail size={19} /><span><strong>{canManageOperation ? "Caixas de e-mail" : "Minha caixa"}</strong><small>{data ? `${data.totals.mailboxes} ${data.totals.mailboxes === 1 ? "caixa" : "caixas"} · ${data.totals.unread} não lidos` : "Carregando uso"}</small></span><IconChevronRight size={16} /></button>
          {canManageOperation ? <button type="button" onClick={() => onRouteChange("domains")}><IconWorld size={19} /><span><strong>Domínios</strong><small>{data ? `${data.totals.domains} domínios cadastrados` : "Carregando domínios"}</small></span><IconChevronRight size={16} /></button> : null}
          {canManageOperation ? <button type="button" onClick={() => onRouteChange("unmatched")}><IconMailQuestion size={19} /><span><strong>Não cadastrados</strong><small>{data ? `${data.totals.unmatchedMessages} capturados · ${data.totals.unmatchedUnread} novos` : "Carregando capturas"}</small></span><IconChevronRight size={16} /></button> : null}
          {session.account.isRoot ? <button type="button" onClick={() => onRouteChange("connections")}><IconPlugConnected size={19} /><span><strong>Conexões IMAP</strong><small>Importe ou mantenha caixas externas sincronizadas</small></span><IconChevronRight size={16} /></button> : null}
          {session.account.isRoot ? <button type="button" onClick={() => onRouteChange("usage")}><IconChartBar size={19} /><span><strong>Uso de e-mail</strong><small>Franquia, limites e volume global</small></span><IconChevronRight size={16} /></button> : null}
        </div>
      </section>
    </div>
  );
}

function ProfileSettings({ session }: { session: SessionResponse }) {
  const activeMailbox = session.mailboxes.find((mailbox) => mailbox.id === session.activeMailboxId);
  return (
    <div>
      <PageHeader eyebrow="Minha conta" title="Perfil" description="Informações usadas para identificar sua conta e o contexto atual de trabalho." />
      <section className="settings-profile-card">
        <div className="settings-profile-card__main"><span>{initials(session.account.name)}</span><div><strong>{session.account.name}</strong><small>{session.account.email}</small><em>{roleName(session)}</em></div></div>
        <dl>
          <div><dt>Empresa ativa</dt><dd>{session.tenant.name}</dd></div>
          <div><dt>Caixa ativa</dt><dd>{activeMailbox?.address || "Nenhuma"}</dd></div>
          <div><dt>Permissão</dt><dd>{roleName(session)}</dd></div>
          <div><dt>Identificador</dt><dd className="settings-mono">{session.account.id.slice(0, 12)}</dd></div>
        </dl>
      </section>
      <aside className="settings-note"><IconCheck size={18} /><div><strong>Uma identidade, várias caixas</strong><p>Seu login continua o mesmo quando você alterna entre empresas, domínios e caixas permitidas.</p></div></aside>
    </div>
  );
}

function PasswordSettings({ onToast }: { onToast: (message: string) => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: SubmitEvent) => {
    event.preventDefault();
    if (newPassword !== confirmation) { setError("A confirmação não corresponde à nova senha."); return; }
    if (newPassword.length < 8) { setError("Use pelo menos 8 caracteres na nova senha."); return; }
    setSaving(true); setError(null);
    try {
      await apiRequest<{ ok: true; message: string }>("/api/v1/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
      setCurrentPassword(""); setNewPassword(""); setConfirmation("");
      onToast("Senha alterada. As outras sessões foram encerradas.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível alterar a senha.");
    } finally { setSaving(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Minha conta" title="Segurança" description="Atualize sua senha sem sair da área de configurações." />
      <form className="settings-form" onSubmit={(event) => void submit(event)}>
        <header><IconLock size={20} /><div><strong>Alterar senha</strong><small>A mudança encerra todas as outras sessões abertas.</small></div></header>
        <label><span>Senha atual</span><input type="password" autoComplete="current-password" value={currentPassword} onInput={(event) => setCurrentPassword(event.currentTarget.value)} required /></label>
        <div className="settings-form__columns">
          <label><span>Nova senha</span><input type="password" autoComplete="new-password" minLength={8} value={newPassword} onInput={(event) => setNewPassword(event.currentTarget.value)} required /><small>Mínimo de 8 caracteres.</small></label>
          <label><span>Confirmar senha</span><input type="password" autoComplete="new-password" minLength={8} value={confirmation} onInput={(event) => setConfirmation(event.currentTarget.value)} required /></label>
        </div>
        {error ? <p className="settings-form__error" role="alert">{error}</p> : null}
        <footer><button className="button button--primary" type="submit" disabled={saving}>{saving ? "Salvando…" : "Salvar nova senha"}</button></footer>
      </form>
    </div>
  );
}

function AppearanceSettings({ theme, onThemeChange }: { theme: PowerMailTheme; onThemeChange: (theme: PowerMailTheme) => void }) {
  return (
    <div>
      <PageHeader eyebrow="Preferências" title="Aparência" description="Escolha como o PowerMail deve aparecer neste dispositivo." />
      <section className="theme-options" aria-label="Escolha de tema">
        <button className={theme === "light" ? "is-active" : ""} type="button" onClick={() => onThemeChange("light")}>
          <span className="theme-preview theme-preview--light"><i /><i /><i /></span>
          <span><IconSun size={20} /><span><strong>Tema claro</strong><small>Leve e confortável para o dia.</small></span>{theme === "light" ? <IconCheck size={17} /> : null}</span>
        </button>
        <button className={theme === "dark" ? "is-active" : ""} type="button" onClick={() => onThemeChange("dark")}>
          <span className="theme-preview theme-preview--dark"><i /><i /><i /></span>
          <span><IconMoon size={20} /><span><strong>Tema escuro</strong><small>Menos brilho em ambientes escuros.</small></span>{theme === "dark" ? <IconCheck size={17} /> : null}</span>
        </button>
      </section>
    </div>
  );
}

function NotificationSettings({
  session,
  permission,
  status,
  loading,
  selectedMailboxIds,
  onActivate,
  onSave,
  onToast,
}: {
  session: SessionResponse;
  permission: MailNotificationPermission;
  status: BackgroundNotificationStatus;
  loading: boolean;
  selectedMailboxIds: string[];
  onActivate: () => Promise<BackgroundNotificationStatus>;
  onSave: (mailboxIds: string[]) => Promise<void>;
  onToast: (message: string) => void;
}) {
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    setDraftIds(selectedMailboxIds.length > 0 ? selectedMailboxIds : [session.activeMailboxId]);
  }, [loading, selectedMailboxIds.join("|"), session.activeMailboxId]);

  const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");
  const visibleMailboxes = session.mailboxes.filter((mailbox) => (
    !normalizedSearch
    || `${mailbox.displayName} ${mailbox.address} ${mailbox.tenantName}`.toLocaleLowerCase("pt-BR").includes(normalizedSearch)
  ));
  const tenantGroups = Array.from(new Set(visibleMailboxes.map((mailbox) => mailbox.tenantId))).map((tenantId) => ({
    tenantId,
    name: visibleMailboxes.find((mailbox) => mailbox.tenantId === tenantId)?.tenantName || "Empresa",
    mailboxes: visibleMailboxes.filter((mailbox) => mailbox.tenantId === tenantId),
  }));

  const toggleMailbox = (mailboxId: string) => {
    setDraftIds((current) => current.includes(mailboxId)
      ? current.filter((candidate) => candidate !== mailboxId)
      : [...current, mailboxId]);
  };
  const submit = async () => {
    if (draftIds.length === 0) {
      setError("Selecione ao menos uma caixa para receber notificações.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draftIds);
      if (status !== "enabled") {
        const result = await onActivate();
        if (result !== "enabled") {
          setError(result === "manual"
            ? "As caixas foram salvas. Libere as notificações nas permissões do navegador para concluir."
            : "As caixas foram salvas, mas este navegador ainda não ativou os avisos.");
          return;
        }
      }
      onToast(`Notificações configuradas para ${draftIds.length} ${draftIds.length === 1 ? "caixa" : "caixas"}.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível salvar as caixas de notificação.");
    } finally {
      setSaving(false);
    }
  };

  const enabled = status === "enabled" && permission === "granted";
  return (
    <div className="notification-preferences">
      <PageHeader eyebrow="Preferências" title="Notificações por caixa" description="Escolha exatamente quais endereços podem avisar esta conta quando um novo e-mail chegar." />
      <section className={enabled ? "notification-preferences__status is-enabled" : "notification-preferences__status"}>
        <span><IconBell size={20} /></span>
        <div><strong>{enabled ? "Avisos em segundo plano ativos" : "Avisos deste navegador ainda não estão ativos"}</strong><small>A seleção de caixas vale para sua conta; a permissão do navegador é configurada em cada dispositivo.</small></div>
        <em>{enabled ? "Ativo" : permission === "denied" ? "Bloqueado" : "Pendente"}</em>
      </section>

      <section className="notification-preferences__picker">
        <header>
          <div><strong>Caixas autorizadas a notificar</strong><small>{draftIds.length} de {session.mailboxes.length} selecionadas</small></div>
          <div>
            <button type="button" onClick={() => setDraftIds(session.mailboxes.map((mailbox) => mailbox.id))}>Selecionar todas</button>
            <button type="button" onClick={() => setDraftIds([session.activeMailboxId])}>Somente a atual</button>
          </div>
        </header>
        <label className="notification-preferences__search"><IconSearch size={16} /><input value={search} onInput={(event) => setSearch(event.currentTarget.value)} placeholder="Buscar caixa, endereço ou empresa" /></label>
        <div className="notification-preferences__groups">
          {tenantGroups.map((group) => (
            <article key={group.tenantId}>
              <header><span><IconBuilding size={16} /></span><strong>{group.name}</strong><small>{group.mailboxes.filter((mailbox) => draftIds.includes(mailbox.id)).length}/{group.mailboxes.length}</small></header>
              <div>
                {group.mailboxes.map((mailbox) => (
                  <label key={mailbox.id}>
                    <input type="checkbox" checked={draftIds.includes(mailbox.id)} onChange={() => toggleMailbox(mailbox.id)} />
                    <span>{initials(mailbox.displayName || mailbox.address)}</span>
                    <span><strong>{mailbox.displayName}</strong><small>{mailbox.address}</small></span>
                    {mailbox.id === session.activeMailboxId ? <em>Em uso</em> : null}
                  </label>
                ))}
              </div>
            </article>
          ))}
          {tenantGroups.length === 0 ? <p>Nenhuma caixa encontrada para esta busca.</p> : null}
        </div>
        {error ? <p className="settings-form__error" role="alert">{error}</p> : null}
        <footer><span>Uma notificação só será enviada se o tenant e a caixa do e-mail coincidirem com esta lista.</span><button className="button button--primary" type="button" disabled={saving || loading} onClick={() => void submit()}>{saving ? "Salvando…" : enabled ? "Salvar preferências" : "Salvar e ativar"}</button></footer>
      </section>
    </div>
  );
}

type SettingsMailboxItem = SettingsOverviewResponse["mailboxes"][number];

function MailboxAccountDialog({ mailbox, session, canManage, onUpdated, onClose, onToast }: {
  mailbox: SettingsMailboxItem;
  session: SessionResponse;
  canManage: boolean;
  onUpdated: (data: SettingsOverviewResponse) => void;
  onClose: () => void;
  onToast: (message: string) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [displayName, setDisplayName] = useState(mailbox.displayName);
  const [role, setRole] = useState<"admin" | "user">(mailbox.access.role === "admin" ? "admin" : "user");
  const [grantAccess, setGrantAccess] = useState(mailbox.access.enabled);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState<"revoke" | "delete" | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ownAccount = mailbox.address.toLocaleLowerCase("en-US") === session.account.email.toLocaleLowerCase("en-US");
  const protectedRole = mailbox.access.role === "root" || mailbox.access.role === "owner";
  const canEditDetails = canManage && !ownAccount && !protectedRole;
  const canRevoke = mailbox.access.enabled
    && mailbox.access.role !== "root"
    && (ownAccount || canManage)
    && (mailbox.access.role !== "owner" || session.account.isRoot);
  const canDelete = session.account.isRoot && mailbox.access.role !== "root";

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  const save = async () => {
    const cleanName = displayName.trim();
    if (!cleanName) {
      setError("Informe o nome de exibição da caixa.");
      return;
    }
    if (password && password.length < 8) {
      setError("A nova senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    if (!mailbox.access.enabled && grantAccess && password.length < 8) {
      setError("Gere ou informe uma senha temporária para liberar o primeiro acesso.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await apiRequest<SettingsOverviewResponse>(`/api/v1/settings/mailboxes/${encodeURIComponent(mailbox.id)}`, {
        method: "PATCH",
        body: JSON.stringify({
          displayName: cleanName,
          role,
          grantAccess,
          ...(password ? { password } : {}),
        }),
      });
      onUpdated(response);
      onToast(!mailbox.access.enabled && grantAccess
        ? "Caixa atualizada e acesso liberado. Entregue a senha temporária por um canal seguro."
        : password
          ? "Usuário atualizado. As sessões antigas foram encerradas."
          : "Dados e permissões atualizados.");
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível atualizar este usuário.");
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await apiRequest<SettingsOverviewResponse>(`/api/v1/settings/mailboxes/${encodeURIComponent(mailbox.id)}/access`, { method: "DELETE" });
      if (ownAccount) {
        onToast("Seu acesso foi removido. A caixa e as mensagens foram preservadas.");
        window.location.assign("/");
        return;
      }
      onUpdated(response);
      onToast(session.account.isRoot
        ? "Acesso removido. A caixa e as mensagens foram preservadas."
        : "Acesso removido, caixa preservada e root notificado por e-mail.");
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível remover o acesso.");
    } finally {
      setSaving(false);
    }
  };

  const removeMailbox = async () => {
    if (deleteConfirmation.trim().toLocaleLowerCase("en-US") !== mailbox.address.toLocaleLowerCase("en-US")) {
      setError("Digite o endereço completo da caixa para confirmar a exclusão.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const response = await apiRequest<SettingsOverviewResponse>(`/api/v1/settings/mailboxes/${encodeURIComponent(mailbox.id)}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmation: deleteConfirmation }),
      });
      onUpdated(response);
      onToast(`A caixa ${mailbox.address} foi excluída definitivamente.`);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível excluir a caixa.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="mailbox-account-dialog"
      aria-labelledby="mailbox-account-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onClose();
      }}
    >
      <header className="mailbox-account-dialog__header">
        <span className="mailbox-account-dialog__avatar">{initials(mailbox.displayName || mailbox.address)}</span>
        <div>
          <small>Conta e caixa de e-mail</small>
          <h2 id="mailbox-account-dialog-title">{mailbox.displayName}</h2>
          <span>{mailbox.address}</span>
        </div>
        <button type="button" aria-label="Fechar" disabled={saving} onClick={onClose}><IconX size={20} /></button>
      </header>

      <div className="mailbox-account-dialog__body">
        {canEditDetails ? (
          <>
            <section className="mailbox-account-dialog__section">
              <header><IconUserEdit size={19} /><div><strong>Identidade</strong><small>Nome exibido na caixa e nas mensagens enviadas.</small></div></header>
              <div className="mailbox-account-dialog__fields">
                <label><span>Nome de exibição</span><input value={displayName} maxLength={100} onInput={(event) => setDisplayName(event.currentTarget.value)} /></label>
                <label><span>Endereço de e-mail</span><input value={mailbox.address} readOnly aria-readonly="true" /></label>
              </div>
            </section>

            {!mailbox.access.enabled ? (
              <label className="mailbox-account-dialog__grant">
                <input type="checkbox" checked={grantAccess} onChange={(event) => setGrantAccess(event.currentTarget.checked)} />
                <span><strong>Liberar login no PowerMail</strong><small>Sem essa opção, a caixa continua recebendo e armazenando mensagens, mas ninguém entra diretamente com este endereço.</small></span>
              </label>
            ) : null}

            {grantAccess ? (
              <section className="mailbox-account-dialog__section">
                <header><IconUserShield size={19} /><div><strong>Permissão e acesso</strong><small>Defina o que esta pessoa pode administrar.</small></div></header>
                <div className="mailbox-account-dialog__fields">
                  <label><span>Nível de acesso</span><select value={role} onChange={(event) => setRole(event.currentTarget.value as "admin" | "user")}><option value="user">Usuário</option><option value="admin">Administrador do domínio</option></select><small>{role === "admin" ? `Pode visualizar e gerenciar as caixas do domínio ${mailbox.domain}.` : "Acessa apenas a própria caixa e as próprias mensagens."}</small></label>
                  <label><span>{mailbox.access.enabled ? "Nova senha, opcional" : "Senha temporária"}</span><div className="settings-password-field"><input type="text" autoComplete="new-password" minLength={8} value={password} placeholder={mailbox.access.enabled ? "Deixe vazio para manter" : "Mínimo de 8 caracteres"} onInput={(event) => setPassword(event.currentTarget.value)} /><button type="button" onClick={() => setPassword(temporaryPassword())}>Gerar senha</button></div><small>{password ? "A troca encerra todas as sessões atuais desta conta." : mailbox.access.enabled ? "A senha atual não será alterada." : "Envie a senha temporária por um canal seguro."}</small></label>
                </div>
              </section>
            ) : null}
          </>
        ) : (
          <aside className="mailbox-account-dialog__protected">
            <IconLock size={19} />
            <div><strong>{ownAccount ? "Esta é a sua conta" : "Acesso protegido"}</strong><p>{ownAccount ? "Altere sua senha na área Segurança. Aqui você pode remover seu próprio acesso, quando permitido." : "A conta root e o proprietário não podem ter nome ou permissão alterados nesta tela."}</p></div>
          </aside>
        )}

        {canRevoke || canDelete ? (
          <section className="mailbox-account-dialog__danger">
            <header><IconAlertTriangle size={19} /><div><strong>Ações de acesso e dados</strong><small>Escolha com atenção: remover acesso e excluir a caixa são operações diferentes.</small></div></header>
            {canRevoke ? <button type="button" onClick={() => { setConfirmation("revoke"); setError(null); }}><IconUserOff size={18} /><span><strong>{ownAccount ? "Remover meu acesso" : "Remover acesso da pessoa"}</strong><small>A caixa e todas as mensagens continuam armazenadas. O login é encerrado e a caixa deixa de aparecer para a pessoa.</small></span></button> : null}
            {canDelete ? <button className="is-destructive" type="button" onClick={() => { setConfirmation("delete"); setError(null); }}><IconTrash size={18} /><span><strong>Excluir caixa definitivamente</strong><small>Exclusivo do root. Remove o endereço, as mensagens e os anexos armazenados.</small></span></button> : null}
          </section>
        ) : null}

        {confirmation === "revoke" ? (
          <section className="mailbox-account-dialog__confirmation" role="alertdialog" aria-labelledby="revoke-access-title">
            <div><IconUserOff size={20} /><div><strong id="revoke-access-title">Confirmar remoção de acesso?</strong><p>A caixa <b>{mailbox.address}</b> será preservada. {session.account.isRoot ? "O histórico ficará registrado para auditoria." : "O root receberá um aviso por e-mail."}</p></div></div>
            <footer><button type="button" disabled={saving} onClick={() => setConfirmation(null)}>Cancelar</button><button className="button button--danger" type="button" disabled={saving} onClick={() => void revoke()}>{saving ? "Removendo…" : "Remover acesso"}</button></footer>
          </section>
        ) : null}

        {confirmation === "delete" ? (
          <section className="mailbox-account-dialog__confirmation mailbox-account-dialog__confirmation--delete" role="alertdialog" aria-labelledby="delete-mailbox-title">
            <div><IconTrash size={20} /><div><strong id="delete-mailbox-title">Excluir caixa e mensagens?</strong><p>Esta ação é definitiva. Digite <b>{mailbox.address}</b> para confirmar.</p></div></div>
            <label><span>Endereço completo</span><input value={deleteConfirmation} autoComplete="off" placeholder={mailbox.address} onInput={(event) => setDeleteConfirmation(event.currentTarget.value)} /></label>
            <footer><button type="button" disabled={saving} onClick={() => { setConfirmation(null); setDeleteConfirmation(""); }}>Cancelar</button><button className="button button--danger" type="button" disabled={saving || deleteConfirmation.trim().toLocaleLowerCase("en-US") !== mailbox.address.toLocaleLowerCase("en-US")} onClick={() => void removeMailbox()}>{saving ? "Excluindo…" : "Excluir definitivamente"}</button></footer>
          </section>
        ) : null}

        {error ? <p className="settings-form__error mailbox-account-dialog__error" role="alert">{error}</p> : null}
      </div>

      <footer className="mailbox-account-dialog__footer">
        <span>{mailbox.messageCount.toLocaleString("pt-BR")} mensagens · {formatBytes(mailbox.storageBytes)} usados</span>
        <div><button type="button" disabled={saving} onClick={onClose}>Fechar</button>{canEditDetails ? <button className="button button--primary" type="button" disabled={saving} onClick={() => void save()}>{saving ? "Salvando…" : "Salvar alterações"}</button> : null}</div>
      </footer>
    </dialog>
  );
}

function MailboxesSettings({ data, loading, session, onCreated, onRouteChange, onToast }: { data: SettingsOverviewResponse | null; loading: boolean; session: SessionResponse; onCreated: (data: SettingsOverviewResponse) => void; onRouteChange: (route: SettingsRoute) => void; onToast: (message: string) => void }) {
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [domainId, setDomainId] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [createAccess, setCreateAccess] = useState(true);
  const [createRole, setCreateRole] = useState<"admin" | "user">("user");
  const [createPassword, setCreatePassword] = useState(() => temporaryPassword());
  const [editingMailboxId, setEditingMailboxId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const domains = data?.domains ?? [];
  const creatableDomains = useMemo(() => domains.filter((domain) => domain.mailboxManagement === "powermail"), [data?.domains]);
  const externalDomains = useMemo(() => domains.filter((domain) => domain.mailboxManagement === "external"), [data?.domains]);
  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const mailboxes = useMemo(() => (data?.mailboxes ?? []).filter((mailbox) => !normalizedQuery || `${mailbox.displayName} ${mailbox.address} ${mailbox.tenantName}`.toLocaleLowerCase("pt-BR").includes(normalizedQuery)), [data?.mailboxes, normalizedQuery]);
  const editingMailbox = data?.mailboxes.find((mailbox) => mailbox.id === editingMailboxId) ?? null;
  useEffect(() => {
    if (!creatableDomains.some((domain) => domain.id === domainId)) {
      setDomainId(creatableDomains[0]?.id ?? "");
    }
  }, [domainId, creatableDomains]);
  const selectedDomain = creatableDomains.find((domain) => domain.id === domainId);
  const submit = async (event: SubmitEvent) => {
    event.preventDefault(); setSaving(true); setError(null);
    try {
      const response = await apiRequest<SettingsOverviewResponse>("/api/v1/settings/mailboxes", { method: "POST", body: JSON.stringify({ domainId, localPart, displayName, ...(createAccess ? { access: { password: createPassword, role: createRole } } : {}) }) });
      onCreated(response); setLocalPart(""); setDisplayName(""); setCreatePassword(temporaryPassword()); setCreating(false); onToast(createAccess ? "Caixa e acesso criados. Entregue a senha temporária ao usuário." : "Nova caixa criada sem acesso individual.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível criar a caixa."); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Operação" title="Caixas de e-mail" description="Crie endereços, defina quem pode entrar e controle o nível de acesso de cada pessoa." action={data?.permissions.canManageMailboxes && creatableDomains.length > 0 ? <button className="button button--primary" type="button" onClick={() => { setCreating((current) => !current); setCreatePassword(temporaryPassword()); setError(null); }}><IconPlus size={16} /> Nova caixa</button> : null} />
      {data?.permissions.canManageMailboxes && externalDomains.length > 0 ? <aside className="external-domain-notice"><IconServer size={20} /><div><strong>Caixas gerenciadas no provedor externo</strong><span>{externalDomains.map((domain) => domain.domain).join(", ")}: novos endereços devem ser criados no provedor de e-mail. No PowerMail você continua configurando acesso, sincronização e permissões.</span></div></aside> : null}
      {creating && creatableDomains.length > 0 ? (
        <form className="settings-create-panel" onSubmit={(event) => void submit(event)}>
          <header><div><strong>Criar caixa de e-mail</strong><small>O endereço ficará disponível no seletor de caixas após a criação.</small></div><button type="button" onClick={() => setCreating(false)}>Cancelar</button></header>
          <div className="settings-create-panel__mailbox"><label><span>Nome de exibição</span><input value={displayName} onInput={(event) => setDisplayName(event.currentTarget.value)} placeholder="Ex.: Financeiro" maxLength={100} required /></label><label><span>Endereço</span><div className="mailbox-address-field"><input value={localPart} onInput={(event) => setLocalPart(event.currentTarget.value)} placeholder="financeiro" maxLength={64} required /><span>@{selectedDomain?.domain}</span></div></label><label><span>Domínio</span><select value={domainId} onChange={(event) => setDomainId(event.currentTarget.value)} required>{creatableDomains.map((domain) => <option value={domain.id} key={domain.id}>{domain.domain} · {domain.tenantName}</option>)}</select></label></div><section className="settings-access-fields"><label className="settings-access-toggle"><input type="checkbox" checked={createAccess} onChange={(event) => setCreateAccess(event.currentTarget.checked)} /><span><strong>Criar acesso ao PowerMail</strong><small>Além de receber mensagens, essa pessoa poderá entrar com o próprio e-mail.</small></span></label>{createAccess ? <div><label><span>Permissão</span><select value={createRole} onChange={(event) => setCreateRole(event.currentTarget.value as "admin" | "user")}><option value="user">Usuário · somente a própria caixa</option><option value="admin">Administrador · gerencia o domínio e as caixas</option></select></label><label><span>Senha temporária</span><div className="settings-password-field"><input type="text" autoComplete="new-password" minLength={8} value={createPassword} onInput={(event) => setCreatePassword(event.currentTarget.value)} required /><button type="button" onClick={() => setCreatePassword(temporaryPassword())}>Gerar outra</button></div><small>Mínimo de 8 caracteres. Envie esta senha por um canal seguro.</small></label></div> : null}</section>
          {error ? <p className="settings-form__error" role="alert">{error}</p> : null}
          <footer><button className="button button--primary" type="submit" disabled={saving}>{saving ? "Criando…" : "Criar caixa"}</button></footer>
        </form>
      ) : null}
      <label className="settings-search"><IconSearch size={17} /><input value={query} onInput={(event) => setQuery(event.currentTarget.value)} placeholder="Buscar caixa, domínio ou empresa" /></label>
      {loading ? <div className="settings-loading">Calculando o uso das caixas…</div> : null}
      {!loading && mailboxes.length === 0 ? <div className="settings-empty"><IconMail size={25} /><strong>Nenhuma caixa encontrada</strong><span>{normalizedQuery ? "Tente outra busca." : "Crie o primeiro endereço para começar."}</span></div> : null}
      <div className="mailbox-admin-list">
        {mailboxes.map((mailbox) => {
          const usage = mailbox.quotaBytes > 0 ? Math.min(100, (mailbox.storageBytes / mailbox.quotaBytes) * 100) : 0;
          const ownAccount = mailbox.address.toLocaleLowerCase("en-US") === session.account.email.toLocaleLowerCase("en-US");
          const accessLabel = !mailbox.access.enabled ? "Sem login" : mailbox.access.role === "root" ? "Root" : mailbox.access.role === "owner" ? "Proprietário" : mailbox.access.role === "admin" ? "Administrador" : mailbox.access.role === "user" ? "Usuário" : "Sem login";
          const canOpenEditor = mailbox.access.role !== "root" && (session.account.isRoot || ownAccount || Boolean(data?.permissions.canManageAccess && mailbox.access.role !== "owner"));
          return <article key={mailbox.id}><span className="mailbox-admin-list__avatar">{initials(mailbox.displayName || mailbox.address)}</span><div className="mailbox-admin-list__identity"><strong>{mailbox.displayName}</strong><span>{mailbox.address}</span><small>{session.account.isRoot ? mailbox.tenantName : mailbox.domain}</small></div><dl><div><dt>Mensagens</dt><dd>{mailbox.messageCount.toLocaleString("pt-BR")}</dd></div><div><dt>Não lidos</dt><dd>{mailbox.unreadCount.toLocaleString("pt-BR")}</dd></div></dl><div className="mailbox-admin-list__usage"><span><strong>{formatBytes(mailbox.storageBytes)}</strong><small>de {formatBytes(mailbox.quotaBytes)}</small></span><i><b style={{ width: `${Math.max(usage, usage > 0 ? 1.5 : 0)}%` }} /></i><em>{usage.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% usado</em></div><div className="mailbox-admin-list__access"><span className={`access-badge access-badge--${mailbox.access.enabled ? mailbox.access.role : "none"}`}><IconLock size={13} /> {accessLabel}</span><div className="mailbox-admin-list__actions">{ownAccount ? <button type="button" onClick={() => onRouteChange("password")}><IconKey size={14} /> Minha senha</button> : null}{canOpenEditor ? <button type="button" onClick={() => setEditingMailboxId(mailbox.id)}><IconUserEdit size={14} /> {mailbox.access.enabled ? ownAccount ? "Gerenciar minha conta" : "Editar usuário" : "Configurar acesso"}</button> : null}</div></div></article>;
        })}
      </div>
      {editingMailbox ? <MailboxAccountDialog key={editingMailbox.id} mailbox={editingMailbox} session={session} canManage={Boolean(data?.permissions.canManageAccess)} onUpdated={onCreated} onClose={() => setEditingMailboxId(null)} onToast={onToast} /> : null}
    </div>
  );
}

function DomainsSettings({ data, loading, session, onCreated, onToast }: { data: SettingsOverviewResponse | null; loading: boolean; session: SessionResponse; onCreated: (data: SettingsOverviewResponse) => void; onToast: (message: string) => void }) {
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [domain, setDomain] = useState("");
  const [tenantId, setTenantId] = useState(session.tenant.id);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: SubmitEvent) => {
    event.preventDefault(); setSaving(true); setError(null);
    try {
      const response = await apiRequest<SettingsOverviewResponse>("/api/v1/settings/domains", { method: "POST", body: JSON.stringify({ domain, tenantId }) });
      onCreated(response); setDomain(""); setCreating(false); onToast("Domínio cadastrado. Agora você pode criar as caixas dele.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Não foi possível cadastrar o domínio."); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <PageHeader eyebrow={session.account.isRoot ? "Administração root" : "Operação"} title="Domínios" description={session.account.isRoot ? "Cadastre domínios e vincule cada um à empresa responsável." : "Acompanhe os domínios vinculados à sua empresa."} action={data?.permissions.canManageDomains ? <button className="button button--primary" type="button" onClick={() => setCreating((current) => !current)}><IconPlus size={16} /> Novo domínio</button> : null} />
      {creating ? <form className="settings-create-panel" onSubmit={(event) => void submit(event)}><header><div><strong>Cadastrar domínio</strong><small>O cadastro organiza as caixas; a ativação de DNS continua indicada pelo status.</small></div><button type="button" onClick={() => setCreating(false)}>Cancelar</button></header><div className="settings-form__columns"><label><span>Domínio</span><input value={domain} onInput={(event) => setDomain(event.currentTarget.value)} placeholder="empresa.com.br" required /></label><label><span>Empresa responsável</span><select value={tenantId} onChange={(event) => setTenantId(event.currentTarget.value)} required>{session.workspaces.map((workspace) => <option value={workspace.id} key={workspace.id}>{workspace.name}</option>)}</select></label></div>{error ? <p className="settings-form__error" role="alert">{error}</p> : null}<footer><button className="button button--primary" type="submit" disabled={saving}>{saving ? "Cadastrando…" : "Cadastrar domínio"}</button></footer></form> : null}
      {loading ? <div className="settings-loading">Carregando domínios…</div> : null}
      {!loading && !data?.domains.length ? <div className="settings-empty"><IconWorld size={25} /><strong>Nenhum domínio cadastrado</strong><span>{session.account.isRoot ? "Cadastre o primeiro domínio para liberar novas caixas." : "Peça ao root para vincular um domínio à sua empresa."}</span></div> : null}
      <div className="domain-admin-grid">
        {data?.domains.map((item) => <article key={item.id}><header><span><IconWorld size={18} /></span><div><strong>{item.domain}</strong><small>{item.tenantName}</small></div><em className={`domain-status domain-status--${item.status}`}>{item.status === "active" ? "Ativo" : item.status === "pending" ? "Configuração pendente" : "Pausado"}</em></header><dl><div><dt>Caixas</dt><dd>{item.mailboxCount}</dd></div><div><dt>Não lidos</dt><dd>{item.unreadCount}</dd></div><div><dt>Uso estimado</dt><dd>{formatBytes(item.storageBytes)}</dd></div></dl><footer>Cadastrado em {new Date(item.createdAt).toLocaleDateString("pt-BR")}</footer></article>)}
      </div>
    </div>
  );
}

function UnmatchedSettings({ data, session }: { data: SettingsOverviewResponse | null; session: SessionResponse }) {
  const [messages, setMessages] = useState<UnmatchedMessagesResponse | null>(null);
  const [domainId, setDomainId] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setSelectedId(null);
    const search = domainId ? `?domainId=${encodeURIComponent(domainId)}` : "";
    void apiRequest<UnmatchedMessagesResponse>(`/api/v1/settings/unmatched${search}`)
      .then((response) => { if (active) setMessages(response); })
      .catch((caught: unknown) => { if (active) setError(caught instanceof Error ? caught.message : "Não foi possível carregar as mensagens capturadas."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [domainId, refreshToken, session.tenant.id]);

  const normalizedQuery = query.trim().toLocaleLowerCase("pt-BR");
  const visibleMessages = useMemo(() => (messages?.data ?? []).filter((message) => (
    !normalizedQuery || `${message.recipientAddress} ${message.senderName} ${message.senderAddress} ${message.subject} ${message.preview}`
      .toLocaleLowerCase("pt-BR")
      .includes(normalizedQuery)
  )), [messages?.data, normalizedQuery]);
  const selectedMessage = messages?.data.find((message) => message.id === selectedId) ?? null;

  const setUnread = async (messageId: string, unread: boolean) => {
    const before = messages?.data.find((message) => message.id === messageId)?.unread;
    if (before === unread) return;
    setMessages((current) => current ? {
      data: current.data.map((message) => message.id === messageId ? { ...message, unread } : message),
      meta: { ...current.meta, unread: Math.max(0, current.meta.unread + (unread ? 1 : -1)) },
    } : current);
    try {
      await apiRequest<void>(`/api/v1/settings/unmatched/${encodeURIComponent(messageId)}`, {
        method: "PATCH",
        body: JSON.stringify({ unread }),
      });
    } catch (caught) {
      setMessages((current) => current ? {
        data: current.data.map((message) => message.id === messageId ? { ...message, unread: Boolean(before) } : message),
        meta: { ...current.meta, unread: Math.max(0, current.meta.unread + (unread ? -1 : 1)) },
      } : current);
      setError(caught instanceof Error ? caught.message : "Não foi possível atualizar a leitura.");
    }
  };

  const openMessage = (messageId: string) => {
    setSelectedId(messageId);
    const message = messages?.data.find((candidate) => candidate.id === messageId);
    if (message?.unread) void setUnread(messageId, false);
  };

  const timestamp = (value: string) => new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(value));

  return (
    <div>
      <PageHeader
        eyebrow="Operação"
        title="Destinatários não cadastrados"
        description="Veja mensagens enviadas ao seu domínio quando ainda não existe uma caixa com aquele endereço."
        action={<button className="button button--secondary" type="button" onClick={() => setRefreshToken((current) => current + 1)} disabled={loading}><IconRefresh size={16} /> {loading ? "Atualizando…" : "Atualizar"}</button>}
      />
      <aside className="settings-catchall-note">
        <IconMailQuestion size={20} />
        <div><strong>Nada se perde por erro de endereço</strong><p>Essas mensagens ficam separadas da caixa de entrada. Criar uma caixa depois não move automaticamente o histórico capturado.</p></div>
        <span>{messages ? `${messages.meta.unread} novos` : "…"}</span>
      </aside>
      <div className="unmatched-toolbar">
        <label className="settings-search"><IconSearch size={17} /><input value={query} onInput={(event) => setQuery(event.currentTarget.value)} placeholder="Buscar destinatário, remetente ou assunto" /></label>
        <label><span>Domínio</span><select value={domainId} onChange={(event) => setDomainId(event.currentTarget.value)}><option value="">Todos os domínios</option>{data?.domains.map((domain) => <option value={domain.id} key={domain.id}>{domain.domain}{session.account.isRoot ? ` · ${domain.tenantName}` : ""}</option>)}</select></label>
      </div>
      {error ? <div className="settings-error" role="alert"><strong>Não foi possível concluir a ação</strong><span>{error}</span></div> : null}
      {loading ? <div className="settings-loading">Buscando mensagens sem caixa…</div> : null}
      {!loading && !error && visibleMessages.length === 0 ? <div className="settings-empty"><IconMailQuestion size={27} /><strong>Nenhuma mensagem capturada</strong><span>{normalizedQuery ? "Tente outra busca ou outro domínio." : "Quando alguém usar um endereço ainda não cadastrado, a mensagem aparecerá aqui."}</span></div> : null}
      {!loading && visibleMessages.length > 0 ? (
        <section className={`unmatched-center${selectedMessage ? " has-selection" : ""}`}>
          <div className="unmatched-list" aria-label="Mensagens para endereços não cadastrados">
            {visibleMessages.map((message) => (
              <button className={`${message.unread ? "is-unread" : ""}${selectedId === message.id ? " is-selected" : ""}`} type="button" key={message.id} onClick={() => openMessage(message.id)}>
                <span className="unmatched-list__avatar">{initials(message.senderName || message.senderAddress)}</span>
                <span className="unmatched-list__copy"><span><strong>{message.subject}</strong><time dateTime={message.receivedAt}>{timestamp(message.receivedAt)}</time></span><b>para {message.recipientAddress}</b><small>{message.senderName} · {message.preview || "Mensagem sem prévia"}</small></span>
                {message.unread ? <i aria-label="Não lida" /> : null}
              </button>
            ))}
          </div>
          <article className="unmatched-reader">
            {selectedMessage ? (
              <>
                <header><div><span>Endereço não cadastrado</span><h2>{selectedMessage.subject}</h2></div><button type="button" onClick={() => setSelectedId(null)} aria-label="Fechar mensagem">×</button></header>
                <dl><div><dt>Destinatário informado</dt><dd>{selectedMessage.recipientAddress}</dd></div><div><dt>Remetente</dt><dd>{selectedMessage.senderName} <small>{selectedMessage.senderAddress}</small></dd></div><div><dt>Recebida em</dt><dd>{timestamp(selectedMessage.receivedAt)}</dd></div>{selectedMessage.attachmentCount > 0 ? <div><dt>Anexos</dt><dd><IconPaperclip size={14} /> {selectedMessage.attachmentCount}</dd></div> : null}</dl>
                <EmailDocumentFrame key={selectedMessage.id} src={`/api/v1/settings/unmatched/${encodeURIComponent(selectedMessage.id)}/body`} title={`Mensagem capturada: ${selectedMessage.subject}`} className="unmatched-reader__document" fallbackParagraphs={selectedMessage.preview ? [selectedMessage.preview] : []} />
                <footer><button type="button" onClick={() => void setUnread(selectedMessage.id, !selectedMessage.unread)}>{selectedMessage.unread ? "Marcar como lida" : "Marcar como não lida"}</button><span>Esta captura não cria uma nova caixa de e-mail.</span></footer>
              </>
            ) : <div className="unmatched-reader__empty"><IconMailQuestion size={30} /><strong>Escolha uma mensagem</strong><span>O conteúdo completo e os dados do destinatário aparecem aqui.</span></div>}
          </article>
        </section>
      ) : null}
    </div>
  );
}

const mailConnectionStatusLabels: Record<MailConnection["status"], string> = {
  pending: "Na fila",
  testing: "Validando",
  ready: "Sincronizada",
  syncing: "Importando",
  paused: "Pausada",
  completed: "Concluída",
  error: "Atenção",
};

function connectionDate(value: string | null): string {
  if (!value) return "Ainda não executada";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(value));
}

function ConnectionCard({ connection, busy, onToggle }: {
  connection: MailConnection;
  busy: boolean;
  onToggle: (connection: MailConnection) => void;
}) {
  const job = connection.latestJob;
  const processed = job?.processedMessages ?? connection.folders.reduce((total, folder) => total + folder.importedMessages + folder.duplicateMessages + folder.failedMessages, 0);
  const total = job?.totalMessages ?? connection.folders.reduce((sum, folder) => sum + folder.totalMessages, 0);
  const progress = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : connection.status === "completed" ? 100 : 0;
  const importedBytes = job?.importedBytes ?? connection.folders.reduce((sum, folder) => sum + folder.importedBytes, 0);
  const canToggle = connection.status !== "completed";

  return (
    <article className={`mail-connection-card mail-connection-card--${connection.status}`}>
      <header>
        <span className="mail-connection-card__icon"><IconServer size={20} /></span>
        <div>
          <span>{connection.mode === "sync" ? "Sincronização contínua" : "Importação única"}</span>
          <strong>{connection.label}</strong>
          <small>{connection.sourceAddress} <IconChevronRight size={12} /> {connection.targetMailboxAddress}</small>
        </div>
        <em>{mailConnectionStatusLabels[connection.status]}</em>
      </header>
      <div className="mail-connection-card__progress">
        <div><span><strong>{progress}%</strong> processado</span><span>{processed.toLocaleString("pt-BR")} de {total.toLocaleString("pt-BR")} mensagens</span></div>
        <span><i style={{ width: `${progress}%` }} /></span>
      </div>
      <dl>
        <div><dt>Armazenado</dt><dd>{formatBytes(importedBytes)}</dd></div>
        <div><dt>Pastas</dt><dd>{connection.folders.length || "—"}</dd></div>
        <div><dt>Última sincronização</dt><dd>{connectionDate(connection.lastSyncedAt)}</dd></div>
        <div><dt>Servidor</dt><dd>{connection.imap.host}:{connection.imap.port}</dd></div>
      </dl>
      {connection.lastError ? <p className="mail-connection-card__error"><IconAlertTriangle size={15} /> {connection.lastError}</p> : null}
      {connection.folders.length ? (
        <details className="mail-connection-folders">
          <summary><span>Detalhes por pasta</span><IconChevronDown size={15} /></summary>
          <div>
            {connection.folders.map((folder) => (
              <article key={folder.path}>
                <div><strong>{folder.path}</strong><small>{folder.status === "idle" ? "Monitorando" : folder.status === "completed" ? "Concluída" : folder.status === "error" ? "Com falha" : "Processando"}</small></div>
                <span>{(folder.importedMessages + folder.duplicateMessages + folder.failedMessages).toLocaleString("pt-BR")} / {folder.totalMessages.toLocaleString("pt-BR")}</span>
                <span>{formatBytes(folder.importedBytes)}</span>
              </article>
            ))}
          </div>
        </details>
      ) : null}
      <footer>
        <span><IconShieldCheck size={15} /> Somente cópia · a origem não é alterada</span>
        {canToggle ? (
          <button type="button" onClick={() => onToggle(connection)} disabled={busy}>
            {connection.syncEnabled ? <IconPlayerPause size={15} /> : <IconPlayerPlay size={15} />}
            {busy ? "Salvando…" : connection.syncEnabled ? "Pausar" : "Retomar"}
          </button>
        ) : <strong>Importação finalizada</strong>}
      </footer>
    </article>
  );
}

const diagnosticStateLabels: Record<MailDiagnosticMessage["state"], string> = {
  queued: "Na fila",
  processing: "Processando",
  completed: "Concluído",
  failed: "Falhou",
  cancelled: "Cancelado",
};

function diagnosticDuration(value: number | null): string {
  if (value == null) return "N/D";
  if (value < 1_000) return "< 1 s";
  const totalSeconds = Math.round(value / 1_000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (totalMinutes < 60) return seconds ? `${totalMinutes} min ${seconds} s` : `${totalMinutes} min`;
  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (totalHours < 24) return minutes ? `${totalHours} h ${minutes} min` : `${totalHours} h`;
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours ? `${days} d ${hours} h` : `${days} d`;
}

function diagnosticDate(value: string | null): string {
  if (!value) return "Aguardando";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
    timeZone: "America/Sao_Paulo",
  }).format(new Date(value));
}

function diagnosticPrimaryTime(message: MailDiagnosticMessage): string {
  if (message.state === "queued") return diagnosticDuration(message.queueWaitMs);
  if (message.state === "processing") return diagnosticDuration(message.processingMs ?? message.totalMs);
  return diagnosticDuration(message.totalMs);
}

function MailDiagnosticsPanel({ connections }: { connections: MailConnection[] }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<MailDiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<MailDiagnosticsPeriod>("24h");
  const [direction, setDirection] = useState<MailDiagnosticsDirection>("all");
  const [state, setState] = useState<MailDiagnosticsState>("all");
  const [connectionId, setConnectionId] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async (foreground: boolean) => {
      if (foreground) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const query = new URLSearchParams({ period, direction, state });
      if (connectionId) query.set("connectionId", connectionId);
      if (search) query.set("search", search);
      try {
        const response = await apiRequest<MailDiagnosticsResponse>(`/api/v1/settings/mail-connection-diagnostics?${query.toString()}`);
        if (!cancelled) setData(response);
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "Não foi possível carregar o diagnóstico técnico.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    void load(true);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible" && navigator.onLine) void load(false);
    }, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [open, period, direction, state, connectionId, search, refreshToken]);

  const healthLabel = data?.summary.health === "healthy"
    ? "Operação saudável"
    : data?.summary.health === "attention"
      ? "Requer atenção"
      : data?.summary.health === "offline"
        ? "Conector sem atividade"
        : "Abrir monitor";

  return (
    <section className={`mail-diagnostics${open ? " is-open" : ""}`}>
      <button className="mail-diagnostics__toggle" type="button" onClick={() => setOpen((current) => !current)} aria-expanded={open}>
        <span className="mail-diagnostics__toggle-icon"><IconChartBar size={19} /></span>
        <span className="mail-diagnostics__toggle-copy">
          <small>Exclusivo Naotho</small>
          <strong>Monitor técnico de e-mail</strong>
          <span>Fila, tempo de processamento e trilha de cada mensagem IMAP e SMTP.</span>
        </span>
        <span className={`mail-diagnostics__health mail-diagnostics__health--${data?.summary.health || "idle"}`}><i /> {healthLabel}</span>
        <IconChevronDown className="mail-diagnostics__chevron" size={17} />
      </button>

      {open ? (
        <div className="mail-diagnostics__body">
          <header className="mail-diagnostics__intro">
            <div><strong>Operação em tempo quase real</strong><span>Atualização automática a cada 15 segundos. Nenhum corpo de e-mail ou credencial é exibido.</span></div>
            <span>{data ? `Leitura de ${diagnosticDate(data.generatedAt)}` : "Coletando telemetria"}</span>
          </header>

          {data ? (
            <div className="mail-diagnostics__metrics" aria-label="Resumo técnico">
              <article><span>Conexões online</span><strong>{data.summary.onlineConnections}<small> / {data.summary.connectionCount}</small></strong><em>{data.summary.syncingConnections} sincronizando</em></article>
              <article><span>Fila IMAP</span><strong>{data.summary.inboundRemaining.toLocaleString("pt-BR")}</strong><em>mensagens restantes</em></article>
              <article><span>Fila SMTP</span><strong>{data.summary.outboundQueued.toLocaleString("pt-BR")}</strong><em>{data.summary.outboundSending} em envio</em></article>
              <article><span>Tempo médio SMTP</span><strong>{diagnosticDuration(data.summary.averageOutboundDeliveryMs)}</strong><em>da fila ao aceite</em></article>
              <article className={data.summary.failedInPeriod > 0 ? "has-alert" : ""}><span>Falhas no período</span><strong>{data.summary.failedInPeriod.toLocaleString("pt-BR")}</strong><em>{data.summary.completedInPeriod.toLocaleString("pt-BR")} concluídas</em></article>
            </div>
          ) : null}

          <form className="mail-diagnostics__filters" onSubmit={(event) => {
            event.preventDefault();
            const next = searchDraft.trim();
            if (next === search) setRefreshToken((current) => current + 1);
            else setSearch(next);
          }}>
            <label><span>Período</span><select value={period} onChange={(event) => setPeriod(event.currentTarget.value as MailDiagnosticsPeriod)}><option value="1h">Última hora</option><option value="24h">Últimas 24 horas</option><option value="7d">Últimos 7 dias</option><option value="30d">Últimos 30 dias</option></select></label>
            <label><span>Conexão</span><select value={connectionId} onChange={(event) => setConnectionId(event.currentTarget.value)}><option value="">Todas as conexões</option>{connections.map((connection) => <option value={connection.id} key={connection.id}>{connection.sourceAddress}</option>)}</select></label>
            <label><span>Fluxo</span><select value={direction} onChange={(event) => setDirection(event.currentTarget.value as MailDiagnosticsDirection)}><option value="all">Entrada e saída</option><option value="inbound">Entrada IMAP</option><option value="outbound">Saída SMTP</option></select></label>
            <label><span>Estado</span><select value={state} onChange={(event) => setState(event.currentTarget.value as MailDiagnosticsState)}><option value="all">Todos os estados</option><option value="queued">Na fila</option><option value="processing">Processando</option><option value="completed">Concluído</option><option value="failed">Com falha</option><option value="cancelled">Cancelado</option></select></label>
            <label className="mail-diagnostics__search"><span>Mensagem ou endereço</span><span><IconSearch size={15} /><input value={searchDraft} onInput={(event) => setSearchDraft(event.currentTarget.value)} placeholder="Assunto, remetente, destinatário…" /></span></label>
            <button type="submit" aria-label="Aplicar busca"><IconSearch size={16} /></button>
            <button type="button" onClick={() => setRefreshToken((current) => current + 1)} disabled={loading || refreshing} aria-label="Atualizar diagnóstico"><IconRefresh className={refreshing ? "is-spinning" : ""} size={16} /></button>
          </form>

          {error ? <div className="settings-error" role="alert"><strong>Não foi possível atualizar o monitor</strong><span>{error}</span></div> : null}
          {loading && !data ? <div className="mail-diagnostics__skeleton" aria-label="Carregando diagnóstico"><i /><i /><i /><i /></div> : null}

          {data ? (
            <>
              <section className="mail-diagnostics__connections">
                <header><div><strong>Execução por conexão</strong><span>Fila histórica, pasta atual, progresso e previsão.</span></div>{data.summary.oldestQueuedAt ? <em>Fila SMTP mais antiga: {diagnosticDuration(data.summary.oldestQueuedAgeMs)}</em> : <em>SMTP sem espera</em>}</header>
                <div>
                  {data.connections.map((connection) => {
                    const progress = connection.totalMessages > 0 ? Math.min(100, Math.round(connection.processedMessages / connection.totalMessages * 100)) : connection.status === "completed" ? 100 : 0;
                    return (
                      <article className={`mail-diagnostics-connection mail-diagnostics-connection--${connection.status}`} key={connection.id}>
                        <header><div><strong>{connection.sourceAddress}</strong><span>{connection.label} · {mailConnectionStatusLabels[connection.status]}</span></div><b>{progress}%</b></header>
                        <span className="mail-diagnostics-connection__bar"><i style={{ width: `${progress}%` }} /></span>
                        <dl>
                          <div><dt>Fila IMAP</dt><dd>{connection.remainingMessages.toLocaleString("pt-BR")}</dd></div>
                          <div><dt>Pasta atual</dt><dd>{connection.currentFolder || "Monitorando"}</dd></div>
                          <div><dt>Decorrido</dt><dd>{diagnosticDuration(connection.importElapsedMs)}</dd></div>
                          <div><dt>Previsão</dt><dd>{diagnosticDuration(connection.estimatedRemainingMs)}</dd></div>
                          <div><dt>SMTP</dt><dd>{connection.outboundQueued} fila · {connection.outboundSending} envio</dd></div>
                          <div><dt>Último sync</dt><dd>{connectionDate(connection.lastSyncedAt)}</dd></div>
                        </dl>
                        {connection.lastError ? <p><IconAlertTriangle size={13} /> {connection.lastError}</p> : null}
                      </article>
                    );
                  })}
                </div>
              </section>

              <section className="mail-diagnostics__messages">
                <header><div><strong>Trilha por mensagem</strong><span>{data.meta.returned} registros no recorte · ativos aparecem primeiro</span></div>{data.meta.hasMore ? <em>Mostrando os {data.meta.limit} mais recentes</em> : null}</header>
                {data.messages.length === 0 ? <div className="mail-diagnostics__empty"><IconInbox size={24} /><strong>Nenhuma mensagem neste recorte</strong><span>Ajuste os filtros ou aguarde a próxima coleta.</span></div> : null}
                <div className="mail-diagnostics__message-list">
                  {data.messages.map((message) => (
                    <details className={`mail-diagnostic-message mail-diagnostic-message--${message.state}`} key={message.id}>
                      <summary>
                        <span className="mail-diagnostic-message__direction">{message.direction === "inbound" ? <IconInbox size={16} /> : <IconSend size={16} />}</span>
                        <span className="mail-diagnostic-message__subject"><strong>{message.subject || "(sem assunto)"}</strong><small>{message.direction === "inbound" ? message.senderAddress : message.recipientAddress}</small></span>
                        <span className="mail-diagnostic-message__connection"><strong>{message.transport.toUpperCase()}</strong><small>{message.connectionLabel}</small></span>
                        <span className={`mail-diagnostic-message__state is-${message.state}`}><i /> {diagnosticStateLabels[message.state]}</span>
                        <span className="mail-diagnostic-message__time"><strong>{diagnosticPrimaryTime(message)}</strong><small>{diagnosticDate(message.updatedAt)}</small></span>
                        <IconChevronDown size={15} />
                      </summary>
                      <div className="mail-diagnostic-message__details">
                        <dl>
                          <div><dt>Caixa PowerMail</dt><dd>{message.mailboxAddress}</dd></div>
                          <div><dt>{message.direction === "inbound" ? "Remetente" : "Destinatário"}</dt><dd>{message.direction === "inbound" ? message.senderAddress : message.recipientAddress}</dd></div>
                          <div><dt>Fila</dt><dd>{diagnosticDuration(message.queueWaitMs)}</dd></div>
                          <div><dt>Processamento</dt><dd>{diagnosticDuration(message.processingMs)}</dd></div>
                          <div><dt>{message.transport === "imap" ? "Origem até cópia" : "Tempo total"}</dt><dd>{diagnosticDuration(message.transport === "imap" ? message.sourceLagMs : message.totalMs)}</dd></div>
                          <div><dt>{message.transport === "imap" ? "Pasta / UID" : "Tentativas"}</dt><dd>{message.transport === "imap" ? `${message.folder || "Não informada"} / ${message.remoteUid ?? "N/D"}` : `${message.attempts} de 5`}</dd></div>
                        </dl>
                        <ol className="mail-diagnostic-timeline">
                          {message.stages.map((stage) => (
                            <li className={`is-${stage.status}`} key={stage.key}>
                              <i />
                              <div><strong>{stage.label}</strong><span>{stage.detail || "Sem detalhe adicional"}</span></div>
                              <time dateTime={stage.at || undefined}>{stage.at ? diagnosticDate(stage.at) : stage.status === "done" ? "Horário não preservado" : "Aguardando"}</time>
                            </li>
                          ))}
                        </ol>
                        {message.error ? <p className="mail-diagnostic-message__error"><IconAlertTriangle size={14} /> {message.error}</p> : null}
                        <footer><span>Mensagem <code>{message.messageId}</code></span><span>{message.providerMessageId ? <>ID do provedor <code>{message.providerMessageId}</code></> : `Conexão ${message.connectionId}`}</span></footer>
                      </div>
                    </details>
                  ))}
                </div>
                <p className="mail-diagnostics__note">Em importações históricas, “Origem até cópia” mede o intervalo entre a data informada pelo servidor IMAP e a indexação no PowerMail. Não representa apenas tempo de rede.</p>
              </section>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function MailConnectionsSettings({ data, loading: settingsLoading, session, onToast }: {
  data: SettingsOverviewResponse | null;
  loading: boolean;
  session: SessionResponse;
  onToast: (message: string) => void;
}) {
  const [connections, setConnections] = useState<MailConnectionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [targetMailboxId, setTargetMailboxId] = useState(session.activeMailboxId);
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState<"sync" | "import">("sync");
  const [sourceAddress, setSourceAddress] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [scope, setScope] = useState<MailInitialSyncScope>("all");

  const loadConnections = () => {
    setLoading(true);
    setError(null);
    void apiRequest<MailConnectionsResponse>("/api/v1/settings/mail-connections")
      .then(setConnections)
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : "Não foi possível carregar as conexões."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadConnections(); }, [session.tenant.id]);
  useEffect(() => {
    if (!targetMailboxId && data?.mailboxes[0]) setTargetMailboxId(data.mailboxes[0].id);
  }, [data?.mailboxes, targetMailboxId]);

  const sourceDomain = sourceAddress.trim().toLocaleLowerCase("en-US").split("@").at(-1) || "";
  const suggestedHost = sourceDomain.includes(".") ? `mail.${sourceDomain}` : "";

  const changeSourceAddress = (value: string) => {
    setUsername((current) => !current || current === sourceAddress ? value : current);
    setSourceAddress(value);
    if (!label) setLabel(value.split("@")[0] || "");
  };

  const submit = async (event: SubmitEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const response = await apiRequest<MailConnectionsResponse>("/api/v1/settings/mail-connections", {
        method: "POST",
        body: JSON.stringify({
          targetMailboxId,
          label,
          mode,
          sourceAddress,
          username,
          password,
          imapHost: imapHost || suggestedHost,
          imapPort: 993,
          imapSecure: true,
          smtpHost: smtpHost || suggestedHost,
          smtpPort: 465,
          smtpSecure: true,
          initialSyncScope: scope,
        }),
      });
      setConnections(response);
      setPassword("");
      setCreating(false);
      onToast(mode === "sync" ? "Conexão criada. A validação IMAP e SMTP será iniciada." : "Importação adicionada à fila com segurança.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível criar a conexão.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (connection: MailConnection) => {
    setBusyId(connection.id);
    setError(null);
    try {
      const response = await apiRequest<MailConnectionsResponse>(`/api/v1/settings/mail-connections/${encodeURIComponent(connection.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !connection.syncEnabled }),
      });
      setConnections(response);
      onToast(connection.syncEnabled ? "Conexão pausada." : "Conexão retomada.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Não foi possível atualizar a conexão.");
    } finally {
      setBusyId(null);
    }
  };

  if (!session.account.isRoot) {
    return <div className="settings-error"><strong>Acesso exclusivo do root</strong><span>Somente o administrador global pode importar caixas de outro provedor.</span></div>;
  }

  return (
    <div className="mail-connections-settings">
      <PageHeader
        eyebrow="Migração e integração"
        title="Conexões IMAP e SMTP"
        description="Traga o histórico de outro provedor e mantenha novas mensagens chegando no PowerMail sem consultar o servidor a cada abertura."
        action={<button className="button button--primary" type="button" onClick={() => setCreating((current) => !current)}><IconPlus size={16} /> {creating ? "Fechar" : "Nova conexão"}</button>}
      />
      <aside className="mail-connections-safety">
        <IconShieldCheck size={22} />
        <div><strong>Migração sem alterar a caixa original</strong><p>O PowerMail usa IMAP em modo somente leitura, faz cópias locais e mantém checkpoints para retomar sem duplicar. Nenhuma mensagem é movida, excluída ou marcada como lida.</p></div>
        <span>TLS 993 / 465</span>
      </aside>
      <MailDiagnosticsPanel connections={connections?.connections ?? []} />
      {creating ? (
        <form className="mail-connection-form" onSubmit={(event) => void submit(event)}>
          <header><span><IconPlugConnected size={19} /></span><div><strong>Conectar uma caixa externa</strong><small>Para HostGator/cPanel, normalmente o usuário é o e-mail completo e os dois servidores usam mail.seudominio.</small></div></header>
          <div className="mail-connection-form__grid">
            <label><span>Caixa de origem</span><input type="email" value={sourceAddress} onInput={(event) => changeSourceAddress(event.currentTarget.value)} placeholder="vendas@empresa.com.br" required /></label>
            <label><span>Destino no PowerMail</span><select value={targetMailboxId} onChange={(event) => setTargetMailboxId(event.currentTarget.value)} required><option value="" disabled>Escolha uma caixa</option>{data?.mailboxes.filter((mailbox) => mailbox.status === "active").map((mailbox) => <option value={mailbox.id} key={mailbox.id}>{mailbox.address} · {mailbox.tenantName}</option>)}</select></label>
            <label><span>Nome da conexão</span><input value={label} onInput={(event) => setLabel(event.currentTarget.value)} placeholder="Vendas Soroemp" maxLength={100} required /></label>
            <label><span>O que deseja fazer</span><select value={mode} onChange={(event) => setMode(event.currentTarget.value as "sync" | "import")}><option value="sync">Importar e manter sincronizada</option><option value="import">Somente importar o histórico</option></select></label>
            <label><span>Usuário IMAP/SMTP</span><input value={username} onInput={(event) => setUsername(event.currentTarget.value)} autoCapitalize="none" autoCorrect="off" required /></label>
            <label><span>Senha da caixa externa</span><input type="password" value={password} onInput={(event) => setPassword(event.currentTarget.value)} autoComplete="new-password" required /><small>Criptografada antes de ser salva.</small></label>
            <label><span>Histórico inicial</span><select value={scope} onChange={(event) => setScope(event.currentTarget.value as MailInitialSyncScope)}><option value="all">Todo o histórico</option><option value="1y">Último ano</option><option value="90d">Últimos 90 dias</option><option value="30d">Últimos 30 dias</option></select></label>
          </div>
          <details className="mail-connection-form__advanced">
            <summary><span>Configuração avançada dos servidores</span><IconChevronDown size={15} /></summary>
            <div><label><span>Servidor IMAP · porta 993</span><input value={imapHost} onInput={(event) => setImapHost(event.currentTarget.value)} placeholder={suggestedHost || "mail.empresa.com.br"} /></label><label><span>Servidor SMTP · porta 465</span><input value={smtpHost} onInput={(event) => setSmtpHost(event.currentTarget.value)} placeholder={suggestedHost || "mail.empresa.com.br"} /></label></div>
          </details>
          {error ? <p className="settings-form__error" role="alert">{error}</p> : null}
          <footer><span><IconShieldCheck size={15} /> A validação SMTP apenas autentica; nenhum e-mail de teste é enviado.</span><button className="button button--primary" type="submit" disabled={saving || settingsLoading || !data?.mailboxes.length}><IconDownload size={16} /> {saving ? "Protegendo e salvando…" : "Validar e iniciar"}</button></footer>
        </form>
      ) : null}
      {!creating && error ? <div className="settings-error" role="alert"><strong>Não foi possível concluir a ação</strong><span>{error}</span></div> : null}
      <div className="mail-connections-toolbar"><div><strong>Conexões configuradas</strong><span>{connections?.connections.length ?? 0} no total</span></div><button type="button" onClick={loadConnections} disabled={loading}><IconRefresh className={loading ? "is-spinning" : ""} size={16} /> {loading ? "Atualizando…" : "Atualizar"}</button></div>
      {loading ? <div className="settings-loading">Carregando conexões seguras…</div> : null}
      {!loading && !connections?.connections.length ? <div className="settings-empty"><IconPlugConnected size={28} /><strong>Nenhuma caixa externa conectada</strong><span>Crie uma conexão para importar mensagens da HostGator ou de outro servidor IMAP.</span></div> : null}
      <div className="mail-connections-list">
        {connections?.connections.map((connection) => <ConnectionCard key={connection.id} connection={connection} busy={busyId === connection.id} onToggle={(item) => void toggle(item)} />)}
      </div>
    </div>
  );
}

const emailUsagePeriods: Array<{ value: EmailUsagePeriod; label: string }> = [
  { value: "24h", label: "Últimas 24 horas" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "month", label: "Mês atual" },
];

function usagePercent(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.max(0, used / limit * 100));
}

function usageNumber(value: number): string {
  return value.toLocaleString("pt-BR");
}

function usageTime(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function usageActivity(value: string | null): string {
  if (!value) return "Sem atividade no período";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function usageBucketLabel(value: string, bucket: "hour" | "day"): string {
  const date = new Date(value);
  return bucket === "hour"
    ? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit" }).format(date)
    : new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" }).format(date);
}

function EmailUsageSettings({ session }: { session: SessionResponse }) {
  const [period, setPeriod] = useState<EmailUsagePeriod>("7d");
  const [data, setData] = useState<EmailUsageResponse | null>(null);
  const [loading, setLoading] = useState(session.account.isRoot);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!session.account.isRoot) return;
    let active = true;
    setLoading(true);
    setError(null);
    void apiRequest<EmailUsageResponse>(`/api/v1/settings/email-usage?period=${encodeURIComponent(period)}`)
      .then((response) => {
        if (!active) return;
        setData(response);
        setExpandedDomains((current) => {
          const available = new Set(response.domains.map((domain) => domain.id));
          const preserved = new Set(Array.from(current).filter((id) => available.has(id)));
          if (preserved.size === 0 && response.domains[0]) preserved.add(response.domains[0].id);
          return preserved;
        });
      })
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : "Não foi possível carregar o uso de e-mail.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [period, reloadKey, session.account.isRoot]);

  if (!session.account.isRoot) {
    return (
      <div className="settings-usage">
        <PageHeader eyebrow="Acesso restrito" title="Uso de e-mail" description="Esta visão reúne dados globais e está disponível somente para o usuário master do PowerMail." />
        <div className="settings-error" role="alert"><strong>Acesso exclusivo do root</strong><span>Entre com a conta master para consultar franquias, limites e uso de todos os domínios.</span></div>
      </div>
    );
  }

  const chartMaximum = Math.max(1, ...(data?.timeline.map((item) => item.inboundMessages + item.sentRecipients + item.failedMessages) ?? [1]));
  const chartLabelStep = Math.max(1, Math.ceil((data?.timeline.length ?? 1) / 7));
  const allExpanded = Boolean(data?.domains.length) && expandedDomains.size === data?.domains.length;
  const toggleDomain = (domainId: string) => {
    setExpandedDomains((current) => {
      const next = new Set(current);
      if (next.has(domainId)) next.delete(domainId);
      else next.add(domainId);
      return next;
    });
  };

  return (
    <div className="settings-usage">
      <PageHeader
        eyebrow="Administração master"
        title="Uso de e-mail"
        description="Acompanhe volume, franquia estimada e limites internos em todos os domínios do PowerMail."
        action={(
          <div className="settings-usage__controls">
            <label>
              <span>Período</span>
              <select value={period} onChange={(event) => setPeriod(event.currentTarget.value as EmailUsagePeriod)}>
                {emailUsagePeriods.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
            </label>
            <button type="button" disabled={loading} onClick={() => setReloadKey((value) => value + 1)} aria-label="Atualizar métricas">
              <IconRefresh className={loading ? "is-spinning" : ""} size={17} />
              <span>Atualizar</span>
            </button>
          </div>
        )}
      />

      {error ? <div className="settings-error" role="alert"><strong>Não foi possível carregar as métricas</strong><span>{error}</span><button type="button" onClick={() => setReloadKey((value) => value + 1)}>Tentar novamente</button></div> : null}

      {!data && loading ? (
        <div className="settings-usage__loading" aria-label="Carregando métricas">
          <span /><span /><span /><span /><span /><span />
        </div>
      ) : null}

      {data ? (
        <div className={loading ? "settings-usage__body is-refreshing" : "settings-usage__body"}>
          <section className="usage-quota-grid" aria-label="Franquias e limites">
            <article className={data.provider.estimatedOverage > 0 ? "usage-quota-card is-over" : "usage-quota-card"}>
              <header><span><IconCloud size={19} /></span><div><small>PROVEDOR</small><strong>Franquia mensal Cloudflare</strong></div><em>Estimativa</em></header>
              <div className="usage-quota-card__value">
                <strong>{usageNumber(data.provider.estimatedMonthUsed)}</strong>
                <span>de {usageNumber(data.provider.includedMonthlyEmails)} envios incluídos</span>
              </div>
              <div className="usage-progress" role="progressbar" aria-label="Uso estimado da franquia mensal" aria-valuemin={0} aria-valuemax={data.provider.includedMonthlyEmails} aria-valuenow={Math.min(data.provider.estimatedMonthUsed, data.provider.includedMonthlyEmails)}>
                <i style={{ width: `${usagePercent(data.provider.estimatedMonthUsed, data.provider.includedMonthlyEmails)}%` }} />
              </div>
              <dl>
                <div><dt>Restante estimado</dt><dd>{usageNumber(data.provider.estimatedMonthRemaining)}</dd></div>
                <div><dt>Últimas 24h</dt><dd>{usageNumber(data.provider.last24HoursUsed)}</dd></div>
                <div><dt>Excedente</dt><dd>{usageNumber(data.provider.estimatedOverage)}</dd></div>
              </dl>
              <p>A cota diária da Cloudflare é dinâmica e não é exposta ao Worker. Os números consideram somente envios registrados pelo PowerMail.</p>
            </article>

            <article className="usage-quota-card usage-quota-card--internal">
              <header><span><IconClock size={19} /></span><div><small>POWERMAIL</small><strong>Proteção por hora</strong></div><em>Agora</em></header>
              <div className="usage-quota-card__value">
                <strong>{usageNumber(data.internalQuota.used)}</strong>
                <span>de {usageNumber(data.internalQuota.limit)} destinatários</span>
              </div>
              <div className="usage-progress" role="progressbar" aria-label="Uso dos limites internos nesta hora" aria-valuemin={0} aria-valuemax={data.internalQuota.limit} aria-valuenow={Math.min(data.internalQuota.used, data.internalQuota.limit)}>
                <i style={{ width: `${usagePercent(data.internalQuota.used, data.internalQuota.limit)}%` }} />
              </div>
              <dl>
                <div><dt>Disponível</dt><dd>{usageNumber(data.internalQuota.remaining)}</dd></div>
                <div><dt>Empresas</dt><dd>{usageNumber(data.internalQuota.tenants.length)}</dd></div>
                <div><dt>Reinicia às</dt><dd>{usageTime(data.internalQuota.resetsAt)}</dd></div>
              </dl>
              <p>O limite é independente para cada empresa e reinicia em uma janela fixa de uma hora.</p>
            </article>
          </section>

          <section className="usage-kpis" aria-label={`Resumo de ${data.period.label}`}>
            <article><span><IconSend size={16} /></span><div><small>Destinatários enviados</small><strong>{usageNumber(data.totals.sentRecipients)}</strong></div></article>
            <article><span><IconInbox size={16} /></span><div><small>Mensagens recebidas</small><strong>{usageNumber(data.totals.inboundMessages)}</strong></div></article>
            <article><span><IconMail size={16} /></span><div><small>Mensagens enviadas</small><strong>{usageNumber(data.totals.outboundMessages)}</strong></div></article>
            <article className={data.totals.failedMessages > 0 ? "has-alert" : ""}><span><IconAlertTriangle size={16} /></span><div><small>Falhas de envio</small><strong>{usageNumber(data.totals.failedMessages)}</strong></div></article>
            <article><span><IconWorld size={16} /></span><div><small>Domínios ativos</small><strong>{usageNumber(data.totals.activeDomains)}</strong></div></article>
            <article><span><IconDatabase size={16} /></span><div><small>Caixas ativas</small><strong>{usageNumber(data.totals.activeMailboxes)}</strong></div></article>
          </section>

          <section className="usage-timeline">
            <header><div><small>EVOLUÇÃO</small><strong>{data.period.label}</strong><span>Volume registrado por {data.period.bucket === "hour" ? "hora" : "dia"}.</span></div><div className="usage-timeline__legend"><span className="is-inbound">Recebidos</span><span className="is-sent">Enviados</span><span className="is-failed">Falhas</span></div></header>
            <div className="usage-timeline__plot" role="img" aria-label={`Gráfico de uso em ${data.period.label.toLocaleLowerCase("pt-BR")}`}>
              {data.timeline.map((item, index) => {
                const total = item.inboundMessages + item.sentRecipients + item.failedMessages;
                const showLabel = index % chartLabelStep === 0 || index === data.timeline.length - 1;
                return (
                  <div className="usage-timeline__column" key={item.startedAt} title={`${usageBucketLabel(item.startedAt, data.period.bucket)} — ${item.inboundMessages} recebidos, ${item.sentRecipients} destinatários enviados e ${item.failedMessages} falhas`}>
                    <div className={total === 0 ? "usage-timeline__stack is-empty" : "usage-timeline__stack"}>
                      <i className="is-inbound" style={{ height: `${item.inboundMessages / chartMaximum * 100}%` }} />
                      <i className="is-sent" style={{ height: `${item.sentRecipients / chartMaximum * 100}%` }} />
                      <i className="is-failed" style={{ height: `${item.failedMessages / chartMaximum * 100}%` }} />
                    </div>
                    <span>{showLabel ? usageBucketLabel(item.startedAt, data.period.bucket) : ""}</span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="usage-domains">
            <header>
              <div><small>DETALHAMENTO</small><strong>Domínios e caixas</strong><span>Uso de {data.period.label.toLocaleLowerCase("pt-BR")}.</span></div>
              <button type="button" onClick={() => setExpandedDomains(allExpanded ? new Set() : new Set(data.domains.map((domain) => domain.id)))}>{allExpanded ? "Recolher todos" : "Expandir todos"}</button>
            </header>
            <div className="usage-domain-list">
              {data.domains.map((domain) => {
                const expanded = expandedDomains.has(domain.id);
                return (
                  <article className={expanded ? "is-expanded" : ""} key={domain.id}>
                    <button className="usage-domain-list__toggle" type="button" aria-expanded={expanded} onClick={() => toggleDomain(domain.id)}>
                      <span className="usage-domain-list__identity"><b>@</b><span><strong>{domain.domain}</strong><small>{domain.tenantName} · {domain.mailboxes.length} {domain.mailboxes.length === 1 ? "caixa" : "caixas"}</small></span></span>
                      <span><small>Recebidos</small><strong>{usageNumber(domain.inboundMessages)}</strong></span>
                      <span><small>Enviados</small><strong>{usageNumber(domain.sentRecipients)}</strong></span>
                      <span><small>Falhas</small><strong>{usageNumber(domain.failedMessages)}</strong></span>
                      <span className="usage-domain-list__quota"><small>Limite da empresa</small><strong>{usageNumber(domain.hourlyUsage.used)} / {usageNumber(domain.hourlyUsage.limit)}</strong><i><b style={{ width: `${usagePercent(domain.hourlyUsage.used, domain.hourlyUsage.limit)}%` }} /></i></span>
                      <IconChevronDown size={18} />
                    </button>
                    {expanded ? (
                      <div className="usage-mailboxes">
                        <div className="usage-mailboxes__head"><span>Caixa</span><span>Recebidos</span><span>Enviados</span><span>Falhas</span><span>Última atividade</span></div>
                        {domain.mailboxes.length > 0 ? domain.mailboxes.map((mailbox) => (
                          <div className="usage-mailboxes__row" key={mailbox.id}>
                            <span><b>{initials(mailbox.displayName)}</b><span><strong>{mailbox.displayName}</strong><small>{mailbox.address}</small></span></span>
                            <span data-label="Recebidos">{usageNumber(mailbox.inboundMessages)}</span>
                            <span data-label="Enviados">{usageNumber(mailbox.sentRecipients)}</span>
                            <span data-label="Falhas" className={mailbox.failedMessages > 0 ? "has-alert" : ""}>{usageNumber(mailbox.failedMessages)}</span>
                            <span data-label="Última atividade">{usageActivity(mailbox.lastActivityAt)}</span>
                          </div>
                        )) : <div className="usage-mailboxes__empty">Nenhuma caixa cadastrada neste domínio.</div>}
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>

          <aside className="usage-notices"><IconAlertTriangle size={17} /><div><strong>Como estes números são calculados</strong>{data.notices.map((notice) => <p key={notice}>{notice}</p>)}</div><span>Atualizado às {usageTime(data.generatedAt)}</span></aside>
        </div>
      ) : null}
    </div>
  );
}

export function SettingsPage({ route, session, theme, onBack, onRouteChange, onSessionRefresh, onThemeChange, onToast, notifications }: SettingsPageProps) {
  const [data, setData] = useState<SettingsOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    void apiRequest<SettingsOverviewResponse>("/api/v1/settings")
      .then((response) => { if (active) setData(response); })
      .catch((caught: unknown) => { if (active) setError(caught instanceof Error ? caught.message : "Não foi possível carregar as configurações."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [session.activeMailboxId, session.tenant.id]);

  const updateData = (response: SettingsOverviewResponse) => {
    setData(response);
    onSessionRefresh();
  };

  return (
    <section className="settings-shell">
      <SettingsNav route={route} session={session} onBack={onBack} onRouteChange={onRouteChange} />
      <main className="settings-content">
        {error ? <div className="settings-error" role="alert"><strong>Não foi possível carregar esta área</strong><span>{error}</span></div> : null}
        {!error && route === "overview" ? <SettingsOverview session={session} data={data} onRouteChange={onRouteChange} /> : null}
        {!error && route === "profile" ? <ProfileSettings session={session} /> : null}
        {!error && route === "password" ? <PasswordSettings onToast={onToast} /> : null}
        {!error && route === "appearance" ? <AppearanceSettings theme={theme} onThemeChange={onThemeChange} /> : null}
        {!error && route === "notifications" ? <NotificationSettings session={session} permission={notifications.permission} status={notifications.status} loading={notifications.loading} selectedMailboxIds={notifications.selectedMailboxIds} onActivate={notifications.onActivate} onSave={notifications.onSave} onToast={onToast} /> : null}
        {!error && route === "mailboxes" ? <MailboxesSettings data={data} loading={loading} session={session} onCreated={updateData} onRouteChange={onRouteChange} onToast={onToast} /> : null}
        {!error && route === "domains" ? <DomainsSettings data={data} loading={loading} session={session} onCreated={updateData} onToast={onToast} /> : null}
        {!error && route === "unmatched" ? <UnmatchedSettings data={data} session={session} /> : null}
        {!error && route === "connections" ? <MailConnectionsSettings data={data} loading={loading} session={session} onToast={onToast} /> : null}
        {!error && route === "usage" ? <EmailUsageSettings session={session} /> : null}
      </main>
    </section>
  );
}
